/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import type { FieldMeta } from '@/components/forms/field.schema';
import type {
  SelectTokenOption,
  Token,
  TokenDefinition,
} from '@/components/ui/TokenizedInput/types';
import { entityConfigs } from '@/lib/entities/entityConfig';
import React from 'react';
import { ZodType, ZodTypeDef } from 'zod';
import {
  defaultFieldIcons,
  filterOperations,
  getDefaultOperationsForType,
  getDefaultTransform,
} from './filter-utils';

// Properly typed field with meta information
export type FieldWithMeta = ZodType & {
  _def: ZodTypeDef & {
    meta?: FieldMeta;
  };
};

// Type guard to check if a field has meta
function hasFieldMeta(field: ZodType): field is FieldWithMeta {
  return Boolean((field as any)._def?.meta);
}

// Safe meta accessor
function getFieldMeta(field: ZodType): FieldMeta | null {
  if (hasFieldMeta(field)) {
    return field._def.meta || null;
  }
  return null;
}

// Check if a Zod field is an enum type
function isZodEnum(field: ZodType): boolean {
  // Check for Zod v4 enum structure
  if ((field as any)._def?.type === 'enum') {
    return true;
  }
  return false;
}

// Extract enum options from Zod enum field
function getEnumOptions(field: ZodType): SelectTokenOption[] | null {
  if (!isZodEnum(field)) return null;

  // Try Zod v4 approach first (.options property)
  if ((field as any).options) {
    const enumValues = (field as any).options;
    return enumValues.map((value: string) => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1), // Capitalize first letter
    }));
  }

  // Fallback to Zod v3 approach
  const enumValues = (field as any)._def?.values || [];
  return enumValues.map((value: string) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1), // Capitalize first letter
  }));
}

// Map field types to token types
function mapFieldTypeToTokenType(
  fieldType?: string,
): 'text' | 'select' | 'date' {
  switch (fieldType) {
    case 'select':
    case 'foreignKey':
    case 'foreignKeyArray':
    case 'boolean':
      return 'select';
    case 'date':
      return 'date';
    case 'text':
    case 'textarea':
    case 'markdown':
    case 'richtext':
    default:
      return 'text';
  }
}

// Get icon for field type
function getFieldIcon(
  meta: FieldMeta,
): React.ComponentType<{ className?: string }> {
  if (meta.filterConfig?.icon) {
    return meta.filterConfig.icon;
  }
  const fieldType =
    ('fieldType' in meta ? meta.fieldType : undefined) || 'default';
  return (
    defaultFieldIcons[fieldType as keyof typeof defaultFieldIcons] ||
    defaultFieldIcons.default
  );
}

export function fieldToTokenDefinition(
  fieldKey: string,
  field: ZodType,
  context?: any,
): TokenDefinition | null {
  const meta = getFieldMeta(field);
  if (!meta || meta.filterConfig?.filterable === false) {
    return null;
  }

  // Map field types to token types
  const fieldType = 'fieldType' in meta ? meta.fieldType : undefined;

  // Auto-detect enum fields and treat them as select
  const isEnum = isZodEnum(field);
  const actualTokenType = isEnum ? 'select' : fieldType;

  if (!actualTokenType) {
    throw new Error(`Field ${fieldKey} has no type`);
  }
  if (actualTokenType === 'hidden' || actualTokenType === 'custom') {
    return null;
  }

  const tokenDef: TokenDefinition = {
    key: fieldKey,
    label: meta.label,
    type: mapFieldTypeToTokenType(actualTokenType),
    icon: getFieldIcon(meta),
    keywords: meta.filterConfig?.keywords || [],
    operations:
      meta.filterConfig?.operations ||
      getDefaultOperationsForType(actualTokenType),
  };

  // Handle foreign keys
  if (fieldType === 'foreignKey' || fieldType === 'foreignKeyArray') {
    if (!meta.entity) {
      throw new Error(`Field ${fieldKey} has no entity`);
    }
    const entityConfig = entityConfigs[meta.entity];
    tokenDef.fetchOptions = (search?: string) =>
      entityConfig.search(search, context);
    tokenDef.fetchOption = entityConfig.getById as any;
    tokenDef.getOptionLabel = entityConfig.getOptionLabel;
    tokenDef.getOptionValue = entityConfig.getOptionValue;
    tokenDef.getOptionKeywords = entityConfig.getOptionKeywords;
    tokenDef.renderItem = (data) => entityConfig.renderBadge(data);
  }

  // Handle select fields
  if (fieldType === 'select') {
    if (meta.options) {
      tokenDef.options = meta.options;
    }
  }

  // Handle auto-detected enum fields
  if (isEnum) {
    const enumOptions = getEnumOptions(field);
    if (enumOptions) {
      tokenDef.options = enumOptions;
    }
  }

  // Handle boolean fields - add true/false options
  if (fieldType === 'boolean') {
    tokenDef.options = [
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' },
    ];
  }

  return tokenDef;
}

// FIXME: implement fuzzy search using remainder
export function generateFilterFunction<T extends Record<string, any>>(
  fields: Record<string, ZodType>,
  context?: any,
) {
  return (
    tokens: Token[],

    remainder: string,
  ) => {
    return (item: T) => {
      let result = tokens.every((token) => {
        const field = fields[token.field];
        if (!field) {
          console.warn(`Field definition not found for ${token.field}`);
          return true;
        }

        const meta = getFieldMeta(field);

        try {
          // Handle virtual fields
          if (meta?.getVirtualValue) {
            const value = meta.getVirtualValue(item, context);

            // Use custom filter if provided
            if (meta.filterConfig?.filterFn) {
              return meta.filterConfig.filterFn(value, token);
            }

            // Apply standard operation
            const operation =
              filterOperations[
                token.operation as keyof typeof filterOperations
              ];
            if (!operation) {
              console.warn(`Unknown operation: ${token.operation}`);
              return true;
            }

            return operation(value, token.value);
          }

          // Check if field exists in the item for non-virtual fields
          if (!(token.field in item)) {
            console.warn(`Field ${token.field} not found in item`);
            return true;
          }

          // Use custom filter if provided
          if (meta?.filterConfig?.filterFn) {
            return meta.filterConfig.filterFn(item[token.field], token);
          }

          // Apply standard filter operation
          const operation =
            filterOperations[token.operation as keyof typeof filterOperations];
          if (!operation) {
            console.warn(`Unknown operation: ${token.operation}`);
            return true;
          }

          // Use explicit transform or derive from fieldType
          const fieldType =
            meta && 'fieldType' in meta ? meta.fieldType : undefined;
          const transform =
            meta?.filterConfig?.transform || getDefaultTransform(fieldType);
          const itemValue = transform(item[token.field]);

          return operation(itemValue, token.value);
        } catch (error) {
          console.error(`Filter error for field ${token.field}:`, error);
          return true; // Skip on error to prevent UI crash
        }
      });
      // If remainder is present. also perform fuzzy search on remainder
      if (remainder && result) {
        const upperCaseRemainder = remainder.toUpperCase();
        result = Object.entries(fields).some(([name, field]) => {
          // for (const [name, field] of Object.entries(fields)) {
          const meta = getFieldMeta(field);
          if (meta?.fieldType?.includes('text')) {
            const transform =
              meta?.filterConfig?.transform ||
              getDefaultTransform(meta.fieldType);
            const itemValue = transform(item[name]);

            return itemValue
              ?.toString()
              .toUpperCase()
              .includes(upperCaseRemainder);
          }
          // }
        });
      }
      return result;
    };
  };
}

// Convert multiple fields to token definitions
export function fieldsToTokenDefinitions(
  fields: Record<string, ZodType>,
  options?: {
    exclude?: string[];
    context?: any;
    customTokens?: TokenDefinition[];
  },
): TokenDefinition[] {
  const tokens: TokenDefinition[] = [];

  for (const [key, field] of Object.entries(fields)) {
    if (options?.exclude?.includes(key)) continue;

    const tokenDef = fieldToTokenDefinition(key, field, options?.context);
    if (tokenDef) {
      tokens.push(tokenDef);
    }
  }

  // Add custom tokens if provided
  if (options?.customTokens) {
    tokens.push(...options.customTokens);
  }

  return tokens;
}
